import React, { useMemo, useState, useEffect, useRef } from "react";

import { parse } from "object-flaser";

import {
    PAYMENT_METHOD_CRYPTO, PAYMENT_METHOD_HAWALA, PAYMENT_METHOD_MISC
} from "const";
import { usePrevious, useWindowWidth } from "hooks";
import { toBase64 } from "utils/helpers";
import { Spacer } from "components/base/Spacer";
import fields, {
    // organization field groups
    customerFields, companyFields, // payment method field groups
    bankTransferFields, paypalFields, cryptoFields, hawalaFields, miscFields, otherTransferSystems
} from "./fields";
import {
    FieldInput, FieldSelect, FieldFile, ModalSelectPicker
} from "./Field";
import * as S from "./styled";

import { Divider, Schema } from "rsuite";
import Form from "components/Form/CompatForm";

const MAX_CHARACTERS_MID = 40;
const MAX_CHARACTERS = 255;
const MAX_DESCRIPTION_CHARACTERS = 1023;

const methodFields = [
    bankTransferFields,
    paypalFields,
    otherTransferSystems,
    otherTransferSystems,
    hawalaFields,
    cryptoFields,
    miscFields
];

const { StringType, NumberType, ArrayType } = Schema.Types;

const maxFileSize = 5; // MB
const maxFiles = 5;

const defaultValue = { ot_key: 1, pm_key: 1 };

export default ({
    show,
    curKey,
    onClose,
    maxWidth = 1000,
    modify = false,
    accountId,
    currency,
    // availableCurrency,

    cryptoList,
    organizationTypeList,
    countryList,
    paymentMethodList,
    createPaymentDetails,
    modifyPaymentDetails,
    getPaymentDetailsList,

    defaultFormValue = null,
}) => {
    const formRef = useRef(null);

    const [localPaymentMethodKey, setLocalPaymentMethodKey] = useState(null);
    const isCrypto = useMemo(() => {
        return PAYMENT_METHOD_CRYPTO === localPaymentMethodKey;
    }, [localPaymentMethodKey]);


    const formModel = useMemo(() => Schema.Model({
        // type fields
        ot_key: NumberType().isRequired("This field is required"),
        pm_key: NumberType()
            .isRequired("This field is required")
            .addRule((value, data) => {
                if (data.ot_key === 2 && value === 5)
                    return false;

                return true;
            }, "This method is not allowed with the \"company\" option"),

        // customer fields
        ...(!isCrypto ? {
            customer_name: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            customer_last_name: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            tax_vat_number: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            customer_cntr_code: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "customer_address.city": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "customer_address.region": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`),
            "customer_address.address": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "customer_address.postal_code": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "customer_phone": StringType().maxLength(MAX_CHARACTERS_MID, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        } : {}),
        ext_files: ArrayType()
            .addRule((value, data) => {
                return (data.ot_key === 2 || (value && value.length > 0)) ? true : false;
            }, "This field is required", true)
            .addRule((value) => value.length > maxFiles ? false : true, `Maximum number of files is ${maxFiles}`)
            .addRule((value) => {
                const names = [];
                for (const file of value) {
                    if (file.blobFile && file.blobFile.size > (1024 * 1024 * maxFileSize))
                        names.push(file.name);
                }
                return !names.length
            }, `File size should not exceed ${maxFileSize} MB`),

        // beneficiary fields
        ...(!isCrypto ? {
            beneficiary_name: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            beneficiary_last_name: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`)
                .addRule((value, data) => {
                    if (!value) {
                        if (data.ot_key === 2 && data.pm_key === 1)
                            return true;

                        return false
                    }

                    return true;
                }, "This field is required", true),
            beneficiary_cntr_code: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "beneficiary_address.region": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`),
            "beneficiary_address.city": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "beneficiary_address.address": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
            "beneficiary_address.postal_code": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        } : {}),
        // bank transfer fields
        bank_name: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        bank_cntr_code: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        "bank_address.region": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`),
        "bank_address.city": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        "bank_address.address": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`),
        "bank_address.postal_code": StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`),
        account_iban_number: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        swift_number: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),
        intermediate_bank_details: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),

        // paypal fields
        paypal_id: StringType().maxLength(MAX_CHARACTERS_MID, `The maximum of this field is ${MAX_CHARACTERS_MID}`).isRequired("This field is required"),

        // crypto fields
        // crypto wallet moved to walletModel
        crypto_currency_key: NumberType().isRequired("This field is required"),
        crypto_network_key: NumberType().isRequired("This field is required"),

        // hawala fields
        hawala_description: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required"),

        // msic fields
        misc_description: StringType().maxLength(MAX_DESCRIPTION_CHARACTERS, `The maximum of this field is ${MAX_DESCRIPTION_CHARACTERS}`).isRequired("This field is required")
    }), [isCrypto]);

    const defaultCryptoCurrency = cryptoList && cryptoList.length ? cryptoList[0].crypto_currency_key : defaultFormValue && defaultFormValue.crypto_currency_key || null;
    const defaultNetworkList = cryptoList && cryptoList.length ? cryptoList.find(({ crypto_currency_key: cck }) => cck === defaultCryptoCurrency).network_list : []
    const defaultCryptoNetwork = defaultNetworkList.length ? defaultNetworkList[0].crypto_network_key : null;

    const defaultUnclearableValues = {
        ...defaultValue,
        crypto_currency_key: defaultCryptoCurrency,
        crypto_network_key: defaultCryptoNetwork
    };
    const _defaultFormValue = {
        ...defaultUnclearableValues,
        ...defaultFormValue
    };

    const formattedDefaultFormValue = _defaultFormValue !== null ? {
        ..._defaultFormValue,
        customer_cntr_code: _defaultFormValue.customer_cntr_code && _defaultFormValue.customer_cntr_code.trim(),
        beneficiary_cntr_code: _defaultFormValue.beneficiary_cntr_code && _defaultFormValue.beneficiary_cntr_code.trim()
    } : null;

    const [formValue, setFormValue] = useState(formattedDefaultFormValue);

    const cryptoCurrencyList = useMemo(() => {
        const currentCryptoList = cryptoList && cryptoList.length ? cryptoList.map(({ crypto_currency_key, crypto_currency_name }) => {
            return { key: crypto_currency_key, name: crypto_currency_name };
        }) : [];
        const currentCrypto = currentCryptoList.find(({ key }) => formValue.crypto_currency_key === key);
        if (!currentCrypto && formValue.crypto_currency_name) {
            currentCryptoList.push({
                key: formValue.crypto_currency_key,
                name: formValue.crypto_currency_name,
                block: true
            });
        }
        return currentCryptoList;
    }, [formValue.pm_key, cryptoList]);

    const [currentCryptoCurrency, setCurrentCryptoCurrency] = useState(defaultCryptoCurrency);
    const [currentCryptoNetwork, setCurrentCryptoNetwork] = useState(null);

    const walletModel = useMemo(() => {
        const cryptoCurrency = cryptoList.find(({ crypto_currency_key }) => crypto_currency_key === formValue.crypto_currency_key);
        const cryptoNetworkList = cryptoCurrency && cryptoCurrency.network_list && cryptoCurrency.network_list.length ? cryptoCurrency.network_list : [];
        const cryptoNetworkByKey = formValue.crypto_network_key ? cryptoNetworkList.find(({ crypto_network_key }) => crypto_network_key === formValue.crypto_network_key) : null;
        const cryptoNetwork = formValue.crypto_network_key && cryptoNetworkByKey ? cryptoNetworkByKey : cryptoNetworkList[0];

        const cryptoNetworkRegex = cryptoNetwork ? cryptoNetwork.crypto_network_regex : "";
        const cryptoNetworkName = cryptoNetwork ? cryptoNetwork.crypto_network_name : "this currency";

        const regexFromBackend = new RegExp(cryptoNetworkRegex);

        return Schema.Model({
            crypto_wallet_number: StringType().maxLength(MAX_CHARACTERS, `The maximum of this field is ${MAX_CHARACTERS}`).isRequired("This field is required")
                .pattern(regexFromBackend, `Wrong format for ${cryptoNetworkName}`)
        });
    }, [formValue]);

    const unitedFormModel = Schema.Model.combine(formModel, walletModel)

    // useEffect(() => {
    //     if (formRef) {
    //         const error = formRef.checkForField?.("crypto_wallet_number");
    //         setFormError(prev => ({ ...prev, crypto_wallet_number: error }));
    //     }
    // }, [unitedFormModel]);

    const prevFormValue = usePrevious(formValue);

    const [activeFields, setActiveFields] = useState([]);
    const resizedWidth = useWindowWidth();
    const [loading, setLoading] = useState(false);

    const networkList = useMemo(() => {
        if (currentCryptoCurrency) {
            const currentCryptoValue = cryptoList.find((currency) => currency.crypto_currency_key === currentCryptoCurrency);
            const currentNetworkList = currentCryptoValue ? currentCryptoValue.network_list : [];

            const resultNetworkList = currentNetworkList.map(({ crypto_network_key, crypto_network_name }) => {
                return { key: crypto_network_key, name: crypto_network_name };
            });
            const savedCryptoValue = resultNetworkList.find(({ key }) => key === formValue.crypto_currency_key);

            if (!savedCryptoValue && formValue.crypto_network_name && !resultNetworkList.find(({ key }) => key === formValue.crypto_network_key)) {
                resultNetworkList.push({
                    key: formValue.crypto_network_key,
                    name: formValue.crypto_network_name,
                    block: true
                });
            }

            return resultNetworkList;
        }
        return [];
    }, [formValue, currentCryptoCurrency]);

    useEffect(() => {
        if (!currentCryptoCurrency && formValue.crypto_currency_key) {
            setCurrentCryptoCurrency(formValue.crypto_currency_key);
        }
    }, [formValue.crypto_currency_key])

    useEffect(() => {
        if (formValue.pm_key && prevFormValue.pm_key !== formValue.pm_key) {
            setLocalPaymentMethodKey(formValue.pm_key);
            // setFormValue({...formValue, crypto_currency_key: null, crypto_network_key: null});
        }
    }, [formValue.pm_key, prevFormValue.pm]);

    useEffect(() => {
        if (formValue.crypto_currency_key !== currentCryptoCurrency) {
            setFormValue({
                ...formValue,
                ...(currentCryptoCurrency !== null ? { crypto_network_key: null } : {})
            });
        }
    }, [currentCryptoCurrency]);

    useEffect(() => {
        if (defaultFormValue) {
            setFormValue(formattedDefaultFormValue);
        }
    }, [defaultFormValue])

    // effect: set active fields
    useEffect(() => {
        const [personal, beneficiary] = setFormFields(formValue.ot_key, formValue.pm_key);
        setActiveFields([...personal, ...beneficiary]);
    }, [isCrypto, formValue.ot_key, formValue.pm_key]);


    // effect: prevent "hawala" && "company" option
    useEffect(() => {
        if (formValue.ot_key === 2 && [PAYMENT_METHOD_HAWALA, PAYMENT_METHOD_MISC].includes(formValue.pm_key)) {
            setFormValue({ ...formValue, pm_key: 1 });
        }
    }, [formValue.ot_key, formValue.pm_key]);


    // submit data
    const handleFormSubmit = async () => {

        const fieldsValidity = [];
        for (const fieldName of activeFields) {
            fieldsValidity.push(formRef.current?.checkForField?.(fieldName));
        }


        if (fieldsValidity.includes(false))
            return;

        setLoading(true);
        const submittedFormValue = formRef.current?.getFormValue?.() || formValue;

        const data = {
            target: {
                account_id: accountId
            },
            ot_key: submittedFormValue.ot_key,
            pm_key: submittedFormValue.pm_key,
            cur_key: curKey || currency
        };

        if (modify && submittedFormValue.id) {
            delete data.ot_key;
            delete data.pm_key;
            data.target = {
                payment_detail_id: submittedFormValue.id
            };
        }

        for (const field of activeFields) {
            if (submittedFormValue[field] === undefined || submittedFormValue[field] === null)
                continue;

            if (field === "ext_files") {
                const files = [];
                for (const file of submittedFormValue[field]) {
                    const fileBase64 = file.blobFile
                        ? await toBase64(file.blobFile)
                        : file.url;
                    const [type, content] = fileBase64.split(",");

                    files.push({
                        file: content,
                        type: type.split(";")[0].split(":")[1],
                        fileKey: file.fileKey,
                        name: file.name,
                        status: file.status
                    });
                }
                data[field] = files;
                continue;
            }

            data[field] = submittedFormValue[field];
        }

        const response = !modify
            ? await createPaymentDetails(parse(data))
            : await modifyPaymentDetails(parse(data));

        if (response) {
            getPaymentDetailsList({ target: { account_id: accountId } });
            onClose(response);
        }

        setLoading(false);
    };


    const setFormFields = (organizationType, paymentMethod) => {
        // company + hawala
        if (organizationType === 2 && paymentMethod === PAYMENT_METHOD_HAWALA) {
            return [[], []];
        }
        // private person + hawala
        if (organizationType === 1 && paymentMethod === PAYMENT_METHOD_HAWALA) {
            return [[], ["hawala_description"]];
        }
        // company + misc
        if (organizationType === 2 && paymentMethod === PAYMENT_METHOD_MISC) {
            return [[], []];
        }
        // private person + misc
        if (organizationType === 1 && paymentMethod == PAYMENT_METHOD_MISC) {
            return [[], ["misc_description"]];
        }

        // default
        const organizationFields = organizationType === 1
            ? isCrypto ? [] : customerFields
            : isCrypto ? [] : companyFields;

        return [organizationFields, methodFields[paymentMethod - 1]];
    };


    const renderFields = (fieldNames = [], formValue) => (
        fieldNames.map((fieldName) => {
            const { type, name, label: defaultLabel, labelId, hideOn, ...props } = fields[fieldName];

            // hide on option
            if (hideOn && Array.isArray(hideOn)) {
                for (const conditionItem of hideOn) {
                    const keys = Object.keys(conditionItem || {});
                    const status = keys.map(key => {
                        return formValue[key] && formValue[key] === conditionItem[key];
                    });

                    if (!status.includes(false))
                        return null;
                }
            }

            // get label
            const label = defaultLabel;

            const disabledCurrencyKeys = cryptoCurrencyList ? cryptoCurrencyList.filter(({ block }) => !!block).map(({ key }) => key) : [];
            const disabledNetworkKeys = networkList ? networkList.filter(({ block }) => !!block).map(({ key }) => key) : [];

            switch (type) {
                case "select_country":
                    return <FieldSelect
                        key={name}
                        name={name}
                        label={label}
                        data={countryList}
                        valueKey="cntr_code"
                        labelKey="name"
                        {...props}
                    />;

                case "file":
                    const label_ = formValue.ot_key === 1
                        ? "Passport scans"
                        : "Certificate of Incorporation";
                    return <FieldFile key={name} name={name} defaultFileList={formValue[name]} label={label_} {...props} />;

                case "select_crypto_currency":
                    return <FieldSelect
                        key={name}
                        name={name}
                        label={label}
                        data={cryptoCurrencyList}
                        disabledItemValues={disabledCurrencyKeys}
                        onChange={(value) => {
                            setCurrentCryptoCurrency(value);
                        }}
                        labelKey="name"
                        valueKey="key"
                        searchable={false}
                        showBadge={disabledCurrencyKeys.find((key) => key === formValue.crypto_currency_key)}
                        badgeContent="Unsupported currency"
                        {...props}
                    />;

                case "select_crypto_network":
                    return <FieldSelect
                        key={name}
                        name={name}
                        label={label}
                        data={networkList}
                        disabled={!currentCryptoCurrency}
                        disabledItemValues={disabledNetworkKeys}
                        onChange={(value) => {
                            setCurrentCryptoNetwork(value);
                        }}
                        labelKey="name"
                        valueKey="key"
                        searchable={false}
                        showBadge={disabledNetworkKeys.find((key) => key === formValue.crypto_network_key)}
                        badgeContent="Unsupported network"
                        {...props}
                    />;


                default:
                    return <FieldInput key={name} name={name} label={label} {...props} />;
            }
        })
    );


    const clearForm = () => {
        setFormValue(defaultUnclearableValues);
    };


    const cleanErrors = () => {
        if (formRef.current && formRef.current.cleanErrors) {
            formRef.current.cleanErrors();
        }
    };
    const currentCrypto = cryptoList.find(({ crypto_currency_key: cck }) => cck === formValue.crypto_currency_key);
    const currentNetworkList = currentCrypto ? currentCrypto.network_list : [];
    const currentNetwork = currentNetworkList && currentNetworkList.length ? currentNetworkList.find(({ crypto_network_key: cnt }) => cnt === formValue.crypto_network_key) : null;
    const disableSubmit = formValue.pm_key === PAYMENT_METHOD_CRYPTO ? !currentCrypto || currentCrypto && !currentNetwork : false;

    return (
        <S.FormModal
            {...{ show, onClose }}
            title={modify
                ? "Modify payment details"
                : "Add new payment details"
            }
            width={resizedWidth > maxWidth ? maxWidth : resizedWidth}
            footer={true}
            onSuccessClose={false}
            successButton="Save changes"
            loading={loading}
            keyboard={false}
            extraDisabled={disableSubmit}
            onSuccess={handleFormSubmit}
            onClose={() => {
                onClose(false);
                clearForm();
                setLocalPaymentMethodKey(null);
            }}
            onExited={() => {
                clearForm();
                setLocalPaymentMethodKey(null);
            }}
        >

            <Form
                ref={formRef}
                model={unitedFormModel}
                formValue={formValue}
                onChange={setFormValue}
            >
                <S.FlexBlock>
                    <S.FlexChild>
                        <S.Group>
                            <S.Label>Choose organization type</S.Label>
                            <Form.Control
                                name="ot_key"
                                accepter={ModalSelectPicker}
                                data={organizationTypeList}
                                labelKey="name"
                                valueKey="ot_key"
                                cleanable={false}
                                searchable={false}
                                disabled={modify}
                                onChange={cleanErrors}
                            />
                        </S.Group>
                    </S.FlexChild>

                    <S.FlexChild>
                        <S.Group>
                            <S.Label>Payment method</S.Label>
                            <Form.Control
                                name="pm_key"
                                accepter={ModalSelectPicker}
                                data={paymentMethodList}
                                labelKey="name"
                                valueKey="pm_key"
                                cleanable={false}
                                searchable={false}
                                disabled={modify}
                                disabledItemValues={formValue.ot_key === 2 ? [PAYMENT_METHOD_HAWALA, PAYMENT_METHOD_MISC] : []}
                                onChange={(value) => {
                                    setLocalPaymentMethodKey(value);
                                    cleanErrors();
                                }}
                            />
                        </S.Group>
                    </S.FlexChild>

                </S.FlexBlock>

                <Spacer size={30} />

                <S.FormColumns>
                    {setFormFields(formValue.ot_key, formValue.pm_key)
                        .map((fieldsGroup, index, arr) => {
                            if (!fieldsGroup.length)
                                return null;

                            return (
                                <>
                                    {index > 0 && arr[0].length > 0 &&
                                        <Divider style={{ width: 0 }} vertical />
                                    }
                                    <S.FormColumn colWidth="50%">
                                        {renderFields(fieldsGroup, formValue)}
                                    </S.FormColumn>
                                </>
                            )
                        })
                    }
                </S.FormColumns>
            </Form>
        </S.FormModal>
    );
};

